// auditClient.ts — append-only audit row writer.
//
// Phase-2 stub: POSTs to `/api/v1/audit/rows` per BACKEND_NOTES.md BE-020 /
// BE-021. When the request fails (offline, 5xx, network), the row is queued
// in `sessionStorage` under `gv:audit:queue` and replayed on next success.
// 365-day retention floor (defence variant) is enforced server-side.

export interface AuditEvent {
  kind: string;
  payload?: Record<string, unknown>;
  errorCode?: string;
  resourceId?: string;
}

interface AuditRow extends AuditEvent {
  /** Queue id; ULID-style monotonic timestamp + random suffix. */
  rowId: string;
  /** Epoch ms client-side timestamp. */
  ts: number;
  /** Best-effort current user id (read from platform-sdk if available). */
  actor?: string;
  /** Best-effort current tenant id. */
  tenantId?: string;
  /** Number of times this row has been retried. */
  attempts: number;
}

const QUEUE_KEY = 'gv:audit:queue';
const ENDPOINT = '/api/v1/audit/rows';
const MAX_QUEUE_SIZE = 500;

function genRowId(): string {
  const t = Date.now().toString(36);
  const r = Math.random().toString(36).slice(2, 10);
  return `${t}-${r}`;
}

function readQueue(): AuditRow[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(rows: AuditRow[]): void {
  if (typeof window === 'undefined') return;
  try {
    const trimmed = rows.slice(-MAX_QUEUE_SIZE);
    window.sessionStorage.setItem(QUEUE_KEY, JSON.stringify(trimmed));
  } catch {
    /* sessionStorage may be disabled — fail-open silently */
  }
}

async function postRow(row: AuditRow): Promise<boolean> {
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(row),
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function flushQueue(): Promise<void> {
  const queue = readQueue();
  if (queue.length === 0) return;
  const survivors: AuditRow[] = [];
  for (const row of queue) {
    const ok = await postRow({ ...row, attempts: row.attempts + 1 });
    if (!ok) survivors.push({ ...row, attempts: row.attempts + 1 });
  }
  writeQueue(survivors);
}

function readActor(): { actor?: string; tenantId?: string } {
  // Optional integration with @geosoft/platform-sdk; safe-guarded behind a
  // try/catch so missing SDK doesn't break the writer.
  try {
    // Dynamic require not used — type imports only; the platform-sdk stub
    // exports getCurrentUser() synchronously.
    // We import lazily via globalThis to avoid forcing a dep cycle here.
    const sdk = (globalThis as { __GV_PLATFORM_SDK__?: { getCurrentUser?: () => { id?: string; tenantId?: string } } }).__GV_PLATFORM_SDK__;
    const u = sdk?.getCurrentUser?.();
    return { actor: u?.id, tenantId: u?.tenantId };
  } catch {
    return {};
  }
}

/**
 * Append an audit row. Always resolves; never throws. Network failures are
 * queued in sessionStorage and re-attempted on next call.
 *
 * Cite: BACKEND_NOTES.md BE-020 (audit table) + BE-021 (audit retention).
 */
export async function appendAuditRow(event: AuditEvent): Promise<void> {
  const meta = readActor();
  const row: AuditRow = {
    ...event,
    rowId: genRowId(),
    ts: Date.now(),
    actor: meta.actor,
    tenantId: meta.tenantId,
    attempts: 0,
  };
  // Try to flush any queued rows first (best-effort).
  await flushQueue();
  const ok = await postRow(row);
  if (!ok) {
    const queue = readQueue();
    queue.push(row);
    writeQueue(queue);
  }
}

/** Test / debug only — do not call from production code. */
export function _resetAuditQueue(): void {
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem(QUEUE_KEY);
    } catch {
      /* ignore */
    }
  }
}
